/**
 * The human-verification challenge for the IN-IDE auth modal
 * (`@molecule/app-auth-modal-react`), plus the solved-state lifecycle the bare
 * {@link CaptchaGate} cannot provide on its own.
 *
 * The API's `POST /users` `requireCaptcha` gate does not care which signup
 * surface a request came from, so the modal — which is what a `/signup` CTA
 * actually opens inside the IDE — needs the same widget the standalone
 * `/signup` page renders. Without it the modal POSTs no token and the user is
 * told to "complete the verification challenge" with no challenge on screen.
 *
 * Why this wrapper exists rather than passing `<CaptchaGate>` directly: the
 * token store is cleared by CaptchaGate's own unmount cleanup, but nothing
 * tells the host that happened. A host holding a plain `solved` boolean keeps
 * it `true` after the modal closes (or after a switch to the login tab, which
 * unmounts the slot), so the next open enables submit against an EMPTY store —
 * exactly the 403 this component exists to prevent. Reporting `false` on both
 * mount and unmount ties the flag to the widget's real lifecycle.
 */

import { useEffect } from 'react'

import { CaptchaGate } from './CaptchaGate.js'

/** Props for {@link AuthModalCaptcha}. */
export interface AuthModalCaptchaProps {
  /**
   * Report whether a verification token is currently held. Called with `false`
   * on mount and unmount (see above), `true` once the challenge is solved, and
   * `false` again when the token expires.
   */
  onSolvedChange: (solved: boolean) => void
}

/**
 * Render the signup challenge for the in-IDE auth modal.
 *
 * @param root0 - Component props.
 * @param root0.onSolvedChange - Solved-state reporter.
 * @returns The challenge widget.
 */
export function AuthModalCaptcha({ onSolvedChange }: AuthModalCaptchaProps) {
  useEffect(() => {
    // A freshly mounted widget is unsolved, and the unmount below clears the
    // shared token store — so never inherit a previous open's `true`.
    onSolvedChange(false)
    return () => onSolvedChange(false)
  }, [onSolvedChange])

  return (
    <CaptchaGate
      action="signup"
      molId="auth-modal-captcha-gate"
      onVerify={(token) => onSolvedChange(token.length > 0)}
      onExpire={() => onSolvedChange(false)}
    />
  )
}
